import { useState, useEffect, useRef } from 'react';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, where, getDocs } from 'firebase/firestore';
import { db, appId } from '../config/firebase';
import { migrateSearchesToNewSchema, needsSearchMigration, migrateProxySearchUrls, needsProxyUrlMigration } from '../utils/searchMigration';
import { unwrapProxySearchUrl } from '../utils/propertyPlatforms';
import { isBazarakiPageUrl, convertBazarakiPageUrl } from '../utils/bazarakiUrlConverter';

export const useTrackedSearches = (userId, isAuthReady) => {
  const [trackedSearches, setTrackedSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasMigrated = useRef(false);

  useEffect(() => {
    if (!isAuthReady || !userId || !db) {
      if (isAuthReady && !userId) {
        setTrackedSearches([]);
      }
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const searchesPath = `artifacts/${appId}/users/${userId}/trackedSearches`;

    const unsubscribe = onSnapshot(
      query(collection(db, searchesPath)),
      (snap) => {
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setTrackedSearches(data);
        setIsLoading(false);
      },
      (err) => {
        console.error("Error fetching tracked searches:", err);
        setError("Could not fetch tracked searches.");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, isAuthReady]);

  // One-time migrations: schema rename, then proxy URL fix
  useEffect(() => {
    if (
      hasMigrated.current ||
      !userId ||
      !db ||
      trackedSearches.length === 0
    ) {
      return;
    }

    const runMigrations = async () => {
      if (needsSearchMigration(trackedSearches)) {
        await migrateSearchesToNewSchema(userId, trackedSearches);
      }
      if (needsProxyUrlMigration(trackedSearches)) {
        await migrateProxySearchUrls(userId, trackedSearches);
      }
    };

    hasMigrated.current = true;
    runMigrations()
      .then(() => {})
      .catch((err) => {
        console.error('Search migration error:', err);
        hasMigrated.current = false;
      });
  }, [trackedSearches, userId]);

  const addTrackedSearch = async (searchData) => {
    if (!userId) throw new Error("User not authenticated");
    const rawUrl = searchData.searchUrl.trim();
    let searchUrl = unwrapProxySearchUrl(rawUrl);

    // Convert Bazaraki page URLs to adverts-geometry API URLs
    if (isBazarakiPageUrl(searchUrl)) {
      const apiUrl = convertBazarakiPageUrl(searchUrl);
      if (!apiUrl) {
        throw new Error("Could not convert this Bazaraki URL. The category may not be supported. Please check the URL and try again.");
      }
      searchUrl = apiUrl;
    }
    
    const searchesPath = `artifacts/${appId}/users/${userId}/trackedSearches`;
    
    // Check if this search URL is already being tracked
    const q = query(
      collection(db, searchesPath),
      where("searchUrl", "==", searchUrl)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      throw new Error("This search URL is already being tracked.");
    }
    
    await addDoc(collection(db, searchesPath), {
      name: searchData.name.trim(),
      searchUrl,
      platform: searchData.platform || 'bazaraki',
      lastScanTimestamp: null,
      lastSeenItemIds: [],
      createdAt: serverTimestamp(),
    });
  };

  const updateTrackedSearch = async (searchId, updates) => {
    if (!userId) throw new Error("User not authenticated");
    
    const searchesPath = `artifacts/${appId}/users/${userId}/trackedSearches`;
    const searchRef = doc(db, searchesPath, searchId);
    await updateDoc(searchRef, {
      ...updates,
      updatedAt: new Date(),
    });
  };

  const deleteTrackedSearch = async (searchId) => {
    if (!userId) throw new Error("User not authenticated");
    
    const searchesPath = `artifacts/${appId}/users/${userId}/trackedSearches`;
    const searchRef = doc(db, searchesPath, searchId);
    await deleteDoc(searchRef);
  };

  return {
    trackedSearches,
    isLoading,
    error,
    addTrackedSearch,
    updateTrackedSearch,
    deleteTrackedSearch,
  };
};
